/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

// @services
import PercentageService from '../../services/percentage.service';

// @components
import DashboardNavbar from '../../components/dashboard/DashboardNavbar';
import Pagination from '../../components/common/Pagination';

// @domain
import {
  IConversation,
  IPagination,
  IParamsCallBack,
  IParamsPercentageRequest,
  IPercentage,
  IPercentageResponse,
} from '../../domain/interfaces/Dashboard';

// @utils
import { USER_ROLES } from '../../utils/ConstantsUtils';


const InmobiliariaDashboard: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<IConversation[]>([]);
  const [totalLeadPrice, setTotalLeadPrice] = useState<number>(0);
  const userRole = localStorage.getItem('user_role') || 'user';
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [pagination, setPagination] = useState<IPagination>({
    currentPage: 1,
    hasNextPage: false,
    hasPreviousPage: false,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0
  });
  const [totalPaid, setTotalPaid] = useState<number>(0);
  const userId = localStorage.getItem('user_id') || '';

  const withoutToken = () => {
    setError('No se pudo obtener el ID de usuario. Por favor, inicia sesión nuevamente.');
    setLoading(false);
    return;
  }

  const getPercentage = async (params: IParamsCallBack = {}) => {
    if (!userId) return withoutToken();
    try {
      setLoading(true);
      const paramsToUse: IParamsPercentageRequest = {
        userId: userId,
        month: params.month || selectedMonth,
        year: params.year || selectedYear,
        page: params.page || pagination.currentPage,
        pageSize: params.pageSize || pagination.pageSize
      };
      const percentage = await PercentageService.getPercentageToBePaidToUser(paramsToUse);
      successCallMethod(percentage.data.data);
      updatePagination(percentage.data);
    } catch (error) {
      toast.error('Error al obtener los datos, intenta de nuevo más tarde.');
      setLoading(false);
    }
  }

  const updatePagination = (pagination: IPercentageResponse) => {
    setPagination({
      currentPage: pagination.currentPage,
      hasNextPage: pagination.hasNextPage,
      hasPreviousPage: pagination.hasPreviousPage,
      pageSize: pagination.pageSize,
      totalCount: pagination.totalCount,
      totalPages: pagination.totalPages
    });
  }

  const successCallMethod = (percentage: IPercentage[]) => {
    const totalPercentage = percentage.reduce((sum, item) => sum + item.price, 0);
    setTotalLeadPrice(totalPercentage);
    const results = percentage.map(item => createObject(item));
    setConversations(results);
    toast.success('Datos obtenidos correctamente.');
    setLoading(false);
  }

  const createObject = (request: IPercentage) => {
    return {
      id: request?.id || '',
      title: request?.serviceName || '',
      userNameRequest: request?.userName || '',
      serviceDate: request?.creationDate ? new Date(new Date(request.creationDate).getTime() - 5 * 60 * 60 * 1000).toLocaleDateString('es-ES', { timeZone: 'America/Bogota' }) : '',
      cityName: request?.city || '',
      price: request?.price || 0,
      businessPrice: request?.price || 0,
      originalRequest: request
    };
  }

  const getTotalPaid = async () => {
    if (!userId) return withoutToken();
    try {
      setLoading(true);
      const totalPaid = await PercentageService.getTotalPaidByUser(userId);
      setTotalPaid(totalPaid.data.total);
      setLoading(false);
    } catch (error) {
      toast.error('Error al obtener el total pagado. Intenta de nuevo más tarde.');
      setLoading(false);
    }
  }

  const callMethod = () => {
    getPercentage();
    getTotalPaid();
  }

  useEffect(() => {
    callMethod();
  }, [userRole]);

  // Función para formatear moneda
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    const params: IParamsCallBack = {
      year: year,
      month: selectedMonth,
      page: 1
    };
    getPercentage(params);
  }

  const handleMonthChange = (month: number) => {
    setSelectedMonth(month);
    const params: IParamsCallBack = {
      month: month,
      year: selectedYear,
      page: 1
    };
    getPercentage(params);
  }

  const handlePageChange = (page: number) => {
    if (pagination.currentPage === page) return;
    if (page < 1 || page > pagination.totalPages) return;
    setPagination({
      ...pagination,
      currentPage: page
    });
    const params: IParamsCallBack = {
      page: page
    };
    getPercentage(params);
  }

  return (
    <div className="min-h-screen bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark">
      <DashboardNavbar />
      <div className="container mx-auto px-4 py-8">

        {/* Cards de información general */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg shadow-md p-6 text-center">
            <h2 className="text-lg font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">
              {userRole === USER_ROLES.INMOBILIARIA ? 'Total de Leads' : 'Servicios del mes actual'}
            </h2>
            <p className="text-3xl font-bold text-xervix-primary">
              {
                conversations.length || <span className="text-gray-400">Sin datos</span>
              }
            </p>
          </div>

          <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg shadow-md p-6 text-center">
            <h2 className="text-lg font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">
              {userRole === USER_ROLES.INMOBILIARIA ? 'Dinero Pagado' : 'Total dinero recibido'}
            </h2>
            <p className="text-3xl font-bold text-green-500">
              {loading ? (
                <span className="text-gray-400">Cargando...</span>
              ) :
                <span className="text-gray-400">{totalPaid ? formatCurrency(totalPaid) : '$0'}</span>
              }
            </p>
          </div>

          <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg shadow-md p-6 text-center">
            <h2 className="text-lg font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">
              {userRole === USER_ROLES.INMOBILIARIA ? 'Dinero por pagar' : 'Dinero por recibir mes actual'}
            </h2>
            <p className="text-3xl font-bold text-blue-500">
              {loading ? (
                <span className="text-gray-400">Cargando...</span>
              ) :
                <span className="text-gray-400">{formatCurrency(totalLeadPrice)}</span>
              }
            </p>
          </div>
        </div>

        {/* Filtros de año y mes */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-1">
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200 mb-4">Filtros</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Año
              </label>
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-200"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Mes
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-200"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <option key={month} value={month}>
                    {new Date(2024, month - 1).toLocaleDateString('es-ES', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tabla de solicitudes de servicio */}
        <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg shadow-md overflow-hidden">
          <h2 className="text-xl font-semibold p-4 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
            Solicitudes de Servicio
          </h2>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Servicio</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Usuario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ciudad</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Precio</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-300">
                      <div className="flex justify-center items-center py-4">
                        <div className="animate-pulse flex space-x-4 w-full max-w-md">
                          <div className="flex-1 space-y-4 py-1">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                            <div className="space-y-2">
                              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : conversations?.length > 0 ? (
                  conversations?.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {request.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {request.userNameRequest}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {request.serviceDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {request.cityName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {formatCurrency(request.price)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-300">
                      {error ? "Error al cargar los datos" : "No hay solicitudes de servicio disponibles"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {conversations?.length > 0 &&
              <section>
                <Pagination
                  pagination={pagination}
                  onPageChange={handlePageChange}
                />
              </section>
            }
          </div>
        </div>
      </div>
    </div>
  );
};


export default InmobiliariaDashboard;
